import React, { useState, useEffect, useCallback } from 'react'
import {
  getMoscowTasks, getMoscowPipeline, initMoscowPipeline,
  updateMoscowTask, uploadMoscowFile, refreshMoscowFileUrl,
  updateCandidate, fetchCandidateById, fetchUserProfiles, addActivityLog,
  sendPipelineFileToGroup, fetchMoscowStay,
} from '../lib/api'

const PHASE_NAMES = {
  1: 'Заезд и обустройство',
  2: 'Документальный блок',
  3: 'Регистрация и идентификация',
  4: 'Связь и финансы',
  5: 'Отправка',
}

const CURRENCIES = ['RUB', 'USD', 'EUR', 'AED', 'ZAR']

const REGIONS = ['Якутия', 'Казань', 'Москва']

// Задачи с накопительными расходами (много записей, пополняемые)
const ACCUMULATIVE_TASKS = ['supplies', 'housing_booking', 'medical_tests', 'pocket_money']

function isTaskLocked() {
  return false
}

function statusIcon(status, locked) {
  if (locked) return <span style={{ fontSize: 16, color: '#ccc' }}>🔒</span>
  if (status === 'done') return <span style={{ fontSize: 16, color: '#639922' }}>✅</span>
  if (status === 'in_progress') return <span style={{ fontSize: 16, color: '#EF9F27' }}>🔄</span>
  return <span style={{ fontSize: 16, color: '#ccc' }}>⭕</span>
}

function fmt(isoStr) {
  if (!isoStr) return ''
  try {
    const d = new Date(isoStr)
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return '' }
}

function fmtDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch { return iso }
}

// ─── Просмотр файла со свежей подписанной ссылкой ────────────────────────

function FileLink({ file }) {
  const [loading, setLoading] = useState(false)

  async function handleClick(e) {
    e.preventDefault()
    if (!file.path) { window.open(file.url, '_blank', 'noreferrer'); return }
    setLoading(true)
    try {
      const freshUrl = await refreshMoscowFileUrl(file.path)
      window.open(freshUrl, '_blank', 'noreferrer')
    } catch {
      window.open(file.url, '_blank', 'noreferrer')
    } finally {
      setLoading(false)
    }
  }

  return (
    <a href={file.url} onClick={handleClick}
      className="file-chip-link"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: loading ? '#aaa' : '#185FA5', textDecoration: 'none', padding: '2px 6px', background: '#EDF4FC', borderRadius: 4, marginRight: 4, marginTop: 2 }}>
      📎 {loading ? 'Открываю...' : file.name}
    </a>
  )
}

// ─── Модал добавления одного расхода (для накопительных задач) ────────────

function AddExpenseEntryModal({ task, candidateId, onSave, onClose }) {
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('RUB')
  const [note, setNote] = useState('')
  const [date, setDate] = useState('')
  const [files, setFiles] = useState([])
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!amount || parseFloat(amount) <= 0) { alert('Укажите сумму'); return }
    if (!date) { alert('Укажите дату'); return }
    setSaving(true)
    try {
      const uploadedFiles = files.length > 0
        ? await Promise.all(files.map(f => uploadMoscowFile(candidateId, task.id, f)))
        : []
      const entry = {
        amount: parseFloat(amount),
        currency,
        note: note || null,
        date: date,
        files: uploadedFiles,
      }
      await onSave(entry)
    } catch (e) {
      alert('Ошибка: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Добавить расход — {task.name}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Сумма *</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="0.00" autoFocus
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 14 }} />
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13 }}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Дата *</div>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} required
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Состав / комментарий</div>
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder="Напр.: хлеб, молоко, бытовая химия"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Файлы (необязательно)</div>
            <input type="file" accept="image/*,.pdf" multiple onChange={e => setFiles(Array.from(e.target.files))} style={{ fontSize: 13 }} />
            {files.length > 0 && <div style={{ fontSize: 11, color: '#639922', marginTop: 4 }}>{files.map(f => f.name).join(', ')}</div>}
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение...' : 'Добавить расход'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Модал редактирования записи расхода ─────────────────────────────────

function EditExpenseEntryModal({ entry, index, task, candidateId, onSave, onDelete, onClose }) {
  const [amount, setAmount] = useState(String(entry.amount))
  const [currency, setCurrency] = useState(entry.currency || 'RUB')
  const [note, setNote] = useState(entry.note || '')
  const [newFiles, setNewFiles] = useState([])
  const existingFiles = entry.files || (entry.file ? [entry.file] : [])
  const [keptFiles, setKeptFiles] = useState(existingFiles)
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      const uploaded = newFiles.length > 0
        ? await Promise.all(newFiles.map(f => uploadMoscowFile(candidateId, task.id, f)))
        : []
      await onSave(index, { ...entry, amount: parseFloat(amount), currency, note: note || null, files: [...keptFiles, ...uploaded], file: undefined })
    } catch (e) {
      alert('Ошибка: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Редактировать расход</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Сумма</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 14 }} />
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13 }}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Состав / комментарий</div>
            <input value={note} onChange={e => setNote(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Файлы</div>
            {keptFiles.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                {keptFiles.map((f, i) => (
                  <span key={i} className="file-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: '#f0f4fa', borderRadius: 4, padding: '1px 5px' }}>
                    <FileLink file={f} />
                    <button onClick={() => setKeptFiles(keptFiles.filter((_, j) => j !== i))} title="Удалить" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e57373', fontSize: 13, padding: '0 2px', lineHeight: 1 }}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <input type="file" accept="image/*,.pdf" multiple onChange={e => setNewFiles(Array.from(e.target.files))} style={{ fontSize: 13 }} />
            {newFiles.length > 0 && <div style={{ fontSize: 11, color: '#639922', marginTop: 4 }}>{newFiles.map(f => f.name).join(', ')}</div>}
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-danger" onClick={() => onDelete(index)}>Удалить</button>
          <button className="btn btn-outline" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Карточка накопительной задачи (Продукты и хоз. товары) ──────────────

function AccumFileAdd({ candidateId, taskId, files, onAddFile }) {
  const [uploading, setUploading] = useState(false)
  async function handleChange(e) {
    const selected = Array.from(e.target.files)
    if (!selected.length) return
    e.target.value = ''
    setUploading(true)
    try {
      const uploaded = await Promise.all(selected.map(f => uploadMoscowFile(candidateId, taskId, f)))
      await onAddFile(taskId, [...files, ...uploaded])
    } catch(err) { alert('Ошибка загрузки: ' + err.message) } finally { setUploading(false) }
  }
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: uploading ? '#aaa' : '#185FA5', cursor: uploading ? 'default' : 'pointer', background: '#f0f4fa', borderRadius: 4, padding: '2px 7px', border: '1px dashed #b0c8e8' }}>
      {uploading ? 'Загрузка...' : '+ файл'}
      <input type="file" multiple style={{ display: 'none' }} disabled={uploading} onChange={handleChange} />
    </label>
  )
}

function AccumulativeTaskCard({ task, pipeline, locked, candidateId, onAddEntry, onEditEntry, onDeleteEntry, onInProgress, onComplete, onRevert, onAddFile, onSendToGroup }) {
  const status = pipeline?.status || 'pending'
  const isDone = status === 'done'
  const entries = pipeline?.expense_entries || []
  const entriesTotal = entries.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const legacyCost = (!entries.length && pipeline?.cost) ? parseFloat(pipeline.cost) : 0
  const total = entriesTotal + legacyCost
  const [reverting, setReverting] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [deleteFileIdx, setDeleteFileIdx] = useState(null)
  const [sendingGroup, setSendingGroup] = useState(false)
  const [sentGroup, setSentGroup] = useState(!!pipeline?.sent_to_group_at)

  async function handleRevert() {
    if (!window.confirm('Отменить выполнение задачи «' + task.name + '»?')) return
    setReverting(true)
    try { await onRevert(task.id) } finally { setReverting(false) }
  }

  return (
    <div className={`mp-task-card${isDone ? ' mp-task-done' : ''}${locked ? ' mp-task-locked' : ''}`} style={{
      background: locked ? '#fafaf8' : isDone ? '#f4f9ef' : '#fff',
      border: `1px solid ${locked ? '#f0f0ec' : isDone ? '#d0e8b8' : '#e8e8e5'}`,
      borderRadius: 10, padding: '10px 12px', marginBottom: 8,
      opacity: locked ? 0.6 : 1,
    }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: entries.length > 0 ? 10 : 0 }}>
        <div style={{ flexShrink: 0 }}>{statusIcon(status, locked)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mp-task-name" style={{ fontSize: 13, fontWeight: 600, color: locked ? '#aaa' : '#1a1a1a' }}>{task.name}</div>
          {(entries.length > 0 || legacyCost > 0) && (
            <div
              onClick={() => setExpanded(o => !o)}
              style={{ fontSize: 11, color: '#639922', fontWeight: 600, marginTop: 2, cursor: 'pointer', userSelect: 'none' }}
            >
              Итого: {total.toLocaleString('ru-RU')} RUB{entries.length > 0 ? ` · ${entries.length} ${entries.length === 1 ? 'запись' : entries.length < 5 ? 'записи' : 'записей'}` : ''} {expanded ? '▲' : '▼'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
          {!locked && !isDone && status === 'pending' && (
            <button onClick={() => onInProgress(task.id)}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0dc', background: '#fff', cursor: 'pointer', color: '#EF9F27' }}>
              В работу
            </button>
          )}
          {!locked && !isDone && (
            <button onClick={() => onComplete(task, pipeline)}
              style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: 'none', background: '#1a1a1a', color: '#fff', cursor: 'pointer' }}>
              Выполнить
            </button>
          )}
          {isDone && (
            <button onClick={handleRevert} disabled={reverting}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #f0d0d0', background: '#fff', cursor: 'pointer', color: '#E24B4A' }}>
              {reverting ? '...' : '↩ Отменить'}
            </button>
          )}
          {!locked && (
            <button onClick={() => onAddEntry(task)}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #d0e8b8', background: '#f4f9ef', cursor: 'pointer', color: '#27500A', fontWeight: 600 }}>
              + Расход
            </button>
          )}
          {onSendToGroup && (pipeline?.files || []).length > 0 && (
            <button disabled={sendingGroup || sentGroup} onClick={async () => {
              setSendingGroup(true)
              try {
                for (const f of (pipeline.files || [])) {
                  await onSendToGroup(f.url, f.name, task.name)
                }
                setSentGroup(true)
              } catch(e) { alert('Ошибка отправки: ' + e.message) }
              finally { setSendingGroup(false) }
            }}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #27ae60', background: sentGroup ? '#eaffea' : '#fff', cursor: (sendingGroup || sentGroup) ? 'default' : 'pointer', color: '#27ae60' }}>
              {sendingGroup ? '...' : sentGroup ? '✓ Отправлено' : '📤 В группу'}
            </button>
          )}
        </div>
      </div>

      {/* Файлы задачи (загруженные напрямую, не через записи расходов) */}
      {(!locked && onAddFile) || (pipeline?.files || []).length > 0 ? (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          {(pipeline?.files || []).map((f, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: '#f0f4fa', borderRadius: 4, padding: '1px 5px' }}>
              <FileLink file={f} />
              {!locked && onAddFile && deleteFileIdx !== i && (
                <button onClick={() => setDeleteFileIdx(i)} title="Удалить" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e57373', fontSize: 13, padding: '0 2px', lineHeight: 1 }}>✕</button>
              )}
              {!locked && onAddFile && deleteFileIdx === i && (
                <span style={{ display: 'inline-flex', gap: 2 }}>
                  <button onClick={async () => { setDeleteFileIdx(null); await onAddFile(task.id, (pipeline.files || []).filter((_, j) => j !== i)) }} style={{ background: '#e57373', color: 'white', border: 'none', borderRadius: 3, padding: '1px 5px', cursor: 'pointer', fontSize: 11 }}>✓</button>
                  <button onClick={() => setDeleteFileIdx(null)} style={{ background: '#eee', border: 'none', borderRadius: 3, padding: '1px 5px', cursor: 'pointer', fontSize: 11 }}>✕</button>
                </span>
              )}
            </span>
          ))}
          {!locked && onAddFile && (
            <AccumFileAdd candidateId={candidateId} taskId={task.id} files={pipeline?.files || []} onAddFile={onAddFile} />
          )}
        </div>
      ) : null}

      {/* Список расходов */}
      {(entries.length > 0 || legacyCost > 0) && expanded && (
        <div style={{ borderTop: '1px solid #f0f0ec', paddingTop: 8 }}>
          {legacyCost > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: entries.length > 0 ? '1px solid #f8f8f6' : 'none' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{legacyCost.toLocaleString('ru-RU')} {pipeline.currency || 'RUB'}</span>
            </div>
          )}
          {entries.map((entry, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: i < entries.length - 1 ? '1px solid #f8f8f6' : 'none' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>
                    {parseFloat(entry.amount).toLocaleString('ru-RU')} {entry.currency || 'RUB'}
                  </span>
                  <span style={{ fontSize: 10, color: '#aaa' }}>{fmtDate(entry.date)}</span>
                </div>
                {entry.note && <div style={{ fontSize: 11, color: '#666', marginTop: 1 }}>{entry.note}</div>}
                {(entry.files?.length > 0 || entry.file) && (
                  <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(entry.files || (entry.file ? [entry.file] : [])).map((f, i) => <FileLink key={i} file={f} />)}
                  </div>
                )}
              </div>
              {!locked && (
                <button onClick={() => onEditEntry(task, i, entry)}
                  style={{ fontSize: 11, color: '#aaa', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}>
                  ✏
                </button>
              )}
            </div>
          ))}
          {/* Итог */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 6, marginTop: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#639922' }}>
              Итого: {total.toLocaleString('ru-RU')} RUB
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Модал выполнения / редактирования обычной задачи ────────────────────

function TaskCompleteModal({ task, pipeline, candidateId, currentUser, users, mode, onSave, onClose }) {
  const isEdit = mode === 'edit'
  const [cost, setCost] = useState(isEdit && pipeline.cost ? String(pipeline.cost) : '')
  const [currency, setCurrency] = useState(pipeline.currency || 'RUB')
  const [notes, setNotes] = useState(isEdit ? (pipeline.notes || '') : '')
  const [regionId, setRegionId] = useState(pipeline.region_id || '')
  const [assignedTo, setAssignedTo] = useState(pipeline.assigned_to || '')
  const [newFiles, setNewFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [existingFiles, setExistingFiles] = useState(pipeline.files || [])

  async function handleSave() {
    setSaving(true)
    try {
      let uploadedFiles = [...existingFiles]
      if (newFiles.length > 0) {
        setUploading(true)
        for (const f of newFiles) uploadedFiles.push(await uploadMoscowFile(candidateId, task.id, f))
        setUploading(false)
      }
      const updates = { files: uploadedFiles, notes: notes || null }
      if (!isEdit) {
        updates.status = 'done'
        updates.completed_at = new Date().toISOString()
        updates.completed_by = currentUser.id
      }
      if (task.requires_cost) { updates.cost = cost ? parseFloat(cost) : null; updates.currency = currency }
      if (task.region_dependent) updates.region_id = regionId || null
      if (assignedTo) updates.assigned_to = assignedTo
      await onSave(updates, cost ? parseFloat(cost) : 0, currency, task.name, isEdit)
    } catch (e) {
      alert('Ошибка: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal modal-sm" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Редактировать: ' : 'Выполнить: '}{task.name}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {task.requires_cost && (
            <div>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>
                {isEdit ? 'Обновить сумму расхода' : 'Сумма расхода'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="number" step="0.01" min="0" value={cost} onChange={e => setCost(e.target.value)}
                  placeholder="0.00"
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 14 }} />
                <select value={currency} onChange={e => setCurrency(e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13 }}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}
          <div>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>{task.file_label || 'Файлы'}</div>
              {existingFiles.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>Прикреплённые файлы:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {existingFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FileLink file={f} />
                      <button onClick={() => setExistingFiles(existingFiles.filter((_, j) => j !== i))}
                        title="Удалить файл"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e57373', fontSize: 14, padding: '2px 4px', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  </div>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                {existingFiles.length > 0 ? 'Добавить ещё:' : 'Прикрепить файлы:'}
              </div>
              <input type="file" multiple onChange={e => setNewFiles(Array.from(e.target.files))} style={{ fontSize: 13 }} />
              {newFiles.length > 0 && (
                <div style={{ fontSize: 11, color: '#639922', marginTop: 4 }}>{newFiles.map(f => f.name).join(', ')}</div>
              )}
            </div>
          {task.region_dependent && (
            <div>
              <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Регион заказчика</div>
              <select value={regionId} onChange={e => setRegionId(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13 }}>
                <option value="">— Выбрать регион —</option>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Комментарий</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="Необязательный комментарий..."
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 500, marginBottom: 6 }}>Ответственный</div>
            <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 13 }}>
              <option value="">— Не назначен —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email || u.id}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || uploading}>
            {uploading ? 'Загрузка...' : saving ? 'Сохранение...' : isEdit ? 'Сохранить изменения' : 'Отметить выполненным'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Обычная карточка задачи ──────────────────────────────────────────────

function TaskCard({ task, pipeline, locked, users, currentUser, candidateId, onComplete, onEdit, onRevert, onInProgress, onAddFile, onAddExpense, onUpdateCost, onSendToGroup }) {
  const status = pipeline?.status || 'pending'
  const isDone = status === 'done'
  const completedByUser = isDone && users.find(u => u.id === pipeline.completed_by)
  const files = pipeline?.files || []
  const [reverting, setReverting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleteFileIdx, setDeleteFileIdx] = useState(null)
  const [editingCost, setEditingCost] = useState(false)
  const [costInput, setCostInput] = useState('')
  const [savingCost, setSavingCost] = useState(false)
  const [sendingGroup, setSendingGroup] = useState(false)
  const [sentGroup, setSentGroup] = useState(!!pipeline?.sent_to_group_at)

  async function handleRevert() {
    if (!window.confirm('Отменить выполнение задачи «' + task.name + '»?')) return
    setReverting(true)
    try { await onRevert(task.id) } finally { setReverting(false) }
  }

  async function handleFileAdd(e) {
    const selected = Array.from(e.target.files)
    if (!selected.length) return
    e.target.value = ''
    setUploading(true)
    try {
      const uploaded = await Promise.all(selected.map(f => uploadMoscowFile(candidateId, task.id, f)))
      await onAddFile(task.id, [...files, ...uploaded])
    } catch (err) { alert('Ошибка загрузки: ' + err.message) }
    finally { setUploading(false) }
  }

  return (
    <div className={`mp-task-card${isDone ? ' mp-task-done' : ''}${locked ? ' mp-task-locked' : ''}`} style={{
      background: locked ? '#fafaf8' : isDone ? '#f4f9ef' : '#fff',
      border: `1px solid ${locked ? '#f0f0ec' : isDone ? '#d0e8b8' : '#e8e8e5'}`,
      borderRadius: 10, padding: '10px 12px', marginBottom: 8,
      opacity: locked ? 0.6 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ marginTop: 1, flexShrink: 0 }}>{statusIcon(status, locked)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mp-task-name" style={{ fontSize: 13, fontWeight: 600, color: locked ? '#aaa' : '#1a1a1a', marginBottom: 2 }}>{task.name}</div>
          {(isDone || (!isDone && pipeline?.cost > 0)) && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              {isDone && <div>Выполнено: {fmt(pipeline.completed_at)}</div>}
              {isDone && completedByUser && <div>Кем: {completedByUser.full_name || completedByUser.email}</div>}
              {pipeline.cost > 0 && (
                <div style={{ color: '#639922', fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {!editingCost ? (
                    <>
                      Расход: {parseFloat(pipeline.cost).toLocaleString('ru-RU')} {pipeline.currency || 'RUB'}
                      {!locked && onUpdateCost && (
                        <button onClick={() => { setCostInput(String(pipeline.cost)); setEditingCost(true) }}
                          style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, border: '1px solid #b0d080', background: '#f4f9ef', color: '#639922', cursor: 'pointer', fontWeight: 400 }}>
                          ✏
                        </button>
                      )}
                    </>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <input type="number" step="0.01" min="0" value={costInput} onChange={e => setCostInput(e.target.value)} autoFocus
                        style={{ width: 90, padding: '2px 6px', borderRadius: 5, border: '1px solid #b0d080', fontSize: 12, fontWeight: 600 }} />
                      <span style={{ fontSize: 11, color: '#888' }}>{pipeline.currency || 'RUB'}</span>
                      <button disabled={savingCost} onClick={async () => {
                        const val = parseFloat(costInput)
                        if (!val || val <= 0) { alert('Укажите сумму'); return }
                        setSavingCost(true)
                        try { await onUpdateCost(task.id, val, pipeline.currency || 'RUB') } catch(e) { alert('Ошибка: ' + e.message) }
                        finally { setSavingCost(false); setEditingCost(false) }
                      }} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, background: '#639922', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                        {savingCost ? '...' : '✓'}
                      </button>
                      <button onClick={() => setEditingCost(false)} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 5, background: '#f0f0ec', border: 'none', cursor: 'pointer' }}>✕</button>
                    </span>
                  )}
                </div>
              )}
              {isDone && pipeline.region_id && <div>Регион: {pipeline.region_id}</div>}
              {isDone && pipeline.notes && <div style={{ fontStyle: 'italic', marginTop: 2, color: '#666' }}>{pipeline.notes}</div>}
            </div>
          )}
          {task.requires_files && (files.length > 0 || !locked) && (
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              {files.map((f, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: '#f0f4fa', borderRadius: 4, padding: '1px 5px' }}>
                  <FileLink file={f} />
                  {!locked && deleteFileIdx !== i && (
                    <button onClick={() => setDeleteFileIdx(i)} title="Удалить" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e57373', fontSize: 13, padding: '0 2px', lineHeight: 1 }}>✕</button>
                  )}
                  {!locked && deleteFileIdx === i && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                      <button onClick={async () => { setDeleteFileIdx(null); await onAddFile(task.id, files.filter((_, j) => j !== i)) }} style={{ background: '#e57373', color: 'white', border: 'none', borderRadius: 3, padding: '1px 5px', cursor: 'pointer', fontSize: 11 }}>✓</button>
                      <button onClick={() => setDeleteFileIdx(null)} style={{ background: '#eee', border: 'none', borderRadius: 3, padding: '1px 5px', cursor: 'pointer', fontSize: 11 }}>✕</button>
                    </span>
                  )}
                </span>
              ))}
              {!locked && (
                <label className="file-add-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: uploading ? '#aaa' : '#185FA5', cursor: uploading ? 'default' : 'pointer', background: '#f0f4fa', borderRadius: 4, padding: '2px 7px', border: '1px dashed #b0c8e8' }}>
                  {uploading ? 'Загрузка...' : '+ файл'}
                  <input type="file" multiple style={{ display: 'none' }} disabled={uploading} onChange={handleFileAdd} />
                </label>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
          {!locked && !isDone && status === 'pending' && (
            <button onClick={() => onInProgress(task.id)}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0dc', background: '#fff', cursor: 'pointer', color: '#EF9F27' }}>
              В работу
            </button>
          )}
          {!locked && !isDone && (
            <button onClick={() => onComplete(task, pipeline)}
              style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: 'none', background: '#1a1a1a', color: '#fff', cursor: 'pointer' }}>
              Выполнить
            </button>
          )}
          {isDone && (
            <>
              <button onClick={() => onEdit(task, pipeline)}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0dc', background: '#fff', cursor: 'pointer', color: '#378ADD' }}>
                ✏ Изменить
              </button>
              <button onClick={handleRevert} disabled={reverting}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #f0d0d0', background: '#fff', cursor: 'pointer', color: '#E24B4A' }}>
                {reverting ? '...' : '↩ Отменить'}
              </button>
            </>
          )}
          {onAddExpense && (
            <button onClick={onAddExpense}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #5b8cf5', background: '#fff', cursor: 'pointer', color: '#5b8cf5' }}>
              + Расход
            </button>
          )}
          {onSendToGroup && files.length > 0 && (
            <button disabled={sendingGroup || sentGroup} onClick={async () => {
              setSendingGroup(true)
              try {
                for (const f of files) {
                  await onSendToGroup(f.url, f.name, task.name)
                }
                setSentGroup(true)
              } catch(e) { alert('Ошибка отправки: ' + e.message) }
              finally { setSendingGroup(false) }
            }}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #27ae60', background: sentGroup ? '#eaffea' : '#fff', cursor: (sendingGroup || sentGroup) ? 'default' : 'pointer', color: sentGroup ? '#27ae60' : '#27ae60' }}>
              {sendingGroup ? '...' : sentGroup ? '✓ Отправлено' : '📤 В группу'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Главный компонент ────────────────────────────────────────────────────

// ─── Даты пребывания в Москве (для распределения расходов по выписке карты …6921) ───
const STAY_SRC = { manual: 'вручную', ticket: 'по билету', pipeline: 'по первой записи оформления', completed: 'оформление завершено' }
function MoscowStayBar({ candidate, currentUser }) {
  const [stay, setStay] = useState(null)
  const [editing, setEditing] = useState(false)
  const [arr, setArr] = useState('')
  const [dep, setDep] = useState('')
  const [saving, setSaving] = useState(false)
  const canEdit = ['admin', 'moscow_operator'].includes(currentUser?.role)
  const load = useCallback(() => { fetchMoscowStay(candidate.id).then(setStay).catch(() => setStay(null)) }, [candidate.id])
  useEffect(() => { load() }, [load])
  if (!stay) return null
  const d = iso => iso ? iso.split('-').reverse().join('.') : '—'
  async function save(updates) {
    setSaving(true)
    try { await updateCandidate(candidate.id, updates); setEditing(false); load() }
    catch (e) { alert('Ошибка: ' + e.message) } finally { setSaving(false) }
  }
  return (
    <div style={{ marginBottom: 14, fontSize: 12, color: '#555', background: '#f9f9f7', borderRadius: 10, padding: '8px 12px' }}>
      {!editing ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>
            🏙 В Москве: с <b>{d(stay.arrival)}</b>{stay.arrival_source && <span style={{ color: '#999' }}> ({STAY_SRC[stay.arrival_source]})</span>}
            {' '}по <b>{stay.departure ? d(stay.departure) : 'сейчас'}</b>{stay.departure_source && <span style={{ color: '#999' }}> ({STAY_SRC[stay.departure_source]})</span>}
            {!stay.arrival && <span style={{ color: '#c0392b' }}> — без даты приезда расходы по выписке на кандидата не делятся</span>}
          </span>
          {canEdit && <button onClick={() => { setArr(stay.arrival || ''); setDep(stay.departure || ''); setEditing(true) }}
            style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>✏ даты</button>}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label>Приезд <input id="moscow-arrival-date" type="date" value={arr} onChange={e => setArr(e.target.value)} /></label>
          <label>Отъезд <input id="moscow-departure-date" type="date" value={dep} onChange={e => setDep(e.target.value)} /></label>
          <button disabled={saving || (arr && dep && dep < arr)} onClick={() => save({ moscow_arrival_date: arr || null, moscow_departure_date: dep || null })}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: 'none', background: '#1a1a1a', color: 'white', cursor: 'pointer' }}>Сохранить</button>
          <button disabled={saving} onClick={() => save({ moscow_arrival_date: null, moscow_departure_date: null })}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>Брать автоматически</button>
          <button onClick={() => setEditing(false)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: 'none', background: '#f0f0ec', cursor: 'pointer' }}>Отмена</button>
          <div style={{ width: '100%', color: '#999', fontSize: 11 }}>Уже загруженные выписки пересчитываются кнопкой «Пересчитать» в журнале выписок (Мой кабинет → Москва)</div>
        </div>
      )}
    </div>
  )
}

export default function MoscowPipeline({ candidate, currentUser, onExpenseAdded, onComplete }) {
  const [pipeline, setPipeline] = useState([])
  const [tasks, setTasks] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeModal, setActiveModal] = useState(null) // { type, task, pipeline, mode?, index?, entry? }

  const load = useCallback(async () => {
    try {
      const [allTasks, profiles] = await Promise.all([getMoscowTasks(), fetchUserProfiles()])
      setTasks(allTasks)
      setUsers(profiles)
      try { await initMoscowPipeline(candidate.id) } catch (e) { console.warn('init:', e.message) }
      const rows = await getMoscowPipeline(candidate.id)
      setPipeline(rows)
    } catch (e) {
      console.error('MoscowPipeline load error', e)
    } finally {
      setLoading(false)
    }
  }, [candidate.id])

  useEffect(() => { load() }, [load])

  const pipelineMap = {}
  for (const row of pipeline) pipelineMap[row.task_id] = row

  const countableTasks = tasks.filter(t => t.id !== 'receipt')
  const totalCnt = countableTasks.length || 24
  const doneCnt = candidate.moscow_completed ? totalCnt : pipeline.filter(r => r.status === 'done' && r.task_id !== 'receipt').length
  const pct = totalCnt > 0 ? Math.round(doneCnt / totalCnt * 100) : 0
  const progColor = pct >= 71 ? '#639922' : pct >= 31 ? '#EF9F27' : '#bbb'

  // Добавить расход в candidate.expenses
  async function addCandidateExpense(amount, currency, label) {
    if (!(amount > 0)) return
    const fresh = await fetchCandidateById(candidate.id)
    const existing = Array.isArray(fresh.expenses) ? fresh.expenses : []
    const newExpenses = [...existing, {
      category: 'Оформление в Москве',
      comment: label,
      amount,
      currency,
      date: new Date().toISOString().split('T')[0],
      operator_id: currentUser.id,
    }]
    await updateCandidate(candidate.id, { expenses: newExpenses })
    if (onExpenseAdded) onExpenseAdded(newExpenses)
  }

  // Обновить существующий расход в candidate.expenses (при редактировании задачи)
  async function updateCandidateExpense(amount, currency, label) {
    const fresh = await fetchCandidateById(candidate.id)
    const existing = Array.isArray(fresh.expenses) ? fresh.expenses : []
    const idx = existing.findIndex(e => e.category === 'Оформление в Москве' && e.comment === label && e.source !== 'statement')
    let newExpenses
    if (idx !== -1) {
      if (amount > 0) {
        newExpenses = existing.map((e, i) => i === idx ? { ...e, amount, currency } : e)
      } else {
        newExpenses = existing.filter((_, i) => i !== idx)
      }
    } else if (amount > 0) {
      newExpenses = [...existing, {
        category: 'Оформление в Москве',
        comment: label,
        amount,
        currency,
        date: new Date().toISOString().split('T')[0],
        operator_id: currentUser.id,
      }]
    } else {
      return
    }
    await updateCandidate(candidate.id, { expenses: newExpenses })
    if (onExpenseAdded) onExpenseAdded(newExpenses)
  }

  async function handleInProgress(taskId) {
    try {
      await updateMoscowTask(candidate.id, taskId, { status: 'in_progress' })
      setPipeline(prev => prev.map(r => r.task_id === taskId ? { ...r, status: 'in_progress' } : r))
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  async function handleRevert(taskId) {
    const updates = { status: 'pending', completed_at: null, completed_by: null }
    try {
      await updateMoscowTask(candidate.id, taskId, updates)
      setPipeline(prev => prev.map(r => r.task_id === taskId ? { ...r, ...updates } : r))
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  async function handleAddFile(taskId, newFiles) {
    try {
      await updateMoscowTask(candidate.id, taskId, { files: newFiles })
      setPipeline(prev => prev.map(r => r.task_id === taskId ? { ...r, files: newFiles } : r))
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  async function handleUpdateCost(taskId, cost, currency) {
    await updateMoscowTask(candidate.id, taskId, { cost, currency })
    setPipeline(prev => prev.map(r => r.task_id === taskId ? { ...r, cost, currency } : r))
  }

  // Обычное выполнение / редактирование задачи
  async function handleSave(updates, cost, currency, taskName, isEdit) {
    const taskId = activeModal.task.id
    try {
      await updateMoscowTask(candidate.id, taskId, updates)
      setPipeline(prev => prev.map(r => r.task_id === taskId ? { ...r, ...updates } : r))
      if (isEdit) {
        await updateCandidateExpense(cost, currency, taskName)
      } else {
        await addCandidateExpense(cost, currency, taskName)
      }
      setActiveModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  // Добавить запись расхода в накопительную задачу
  async function handleAddExpenseEntry(entry) {
    const taskId = activeModal.task.id
    const row = pipelineMap[taskId] || {}
    const existing = row.expense_entries || []
    const newEntries = [...existing, entry]
    const newTotal = newEntries.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
    try {
      const updates = { expense_entries: newEntries, cost: newTotal, currency: entry.currency }
      if (row.status === 'pending') updates.status = 'in_progress'
      await updateMoscowTask(candidate.id, taskId, updates)
      setPipeline(prev => prev.map(r => r.task_id === taskId ? { ...r, ...updates } : r))
      await addCandidateExpense(entry.amount, entry.currency, activeModal.task.name + (entry.note ? ': ' + entry.note : ''))
      setActiveModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  // Редактировать запись расхода накопительной задачи
  async function handleEditExpenseEntry(index, updatedEntry) {
    const taskId = activeModal.task.id
    const row = pipelineMap[taskId] || {}
    const existing = [...(row.expense_entries || [])]
    existing[index] = updatedEntry
    const newTotal = existing.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
    try {
      const updates = { expense_entries: existing, cost: newTotal }
      await updateMoscowTask(candidate.id, taskId, updates)
      setPipeline(prev => prev.map(r => r.task_id === taskId ? { ...r, ...updates } : r))
      setActiveModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  // Удалить запись расхода
  async function handleDeleteExpenseEntry(index) {
    if (!window.confirm('Удалить эту запись расхода?')) return
    const taskId = activeModal.task.id
    const row = pipelineMap[taskId] || {}
    const existing = (row.expense_entries || []).filter((_, i) => i !== index)
    const newTotal = existing.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
    try {
      const updates = { expense_entries: existing, cost: newTotal || null }
      await updateMoscowTask(candidate.id, taskId, updates)
      setPipeline(prev => prev.map(r => r.task_id === taskId ? { ...r, ...updates } : r))
      setActiveModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: 13 }}>Загрузка пайплайна...</div>
  )

  return (
    <div>
      <MoscowStayBar candidate={candidate} currentUser={currentUser} />
      {/* Прогресс */}
      <div className="mp-summary-bar" style={{ marginBottom: 20, background: '#f9f9f7', borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Прогресс оформления</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: progColor }}>{doneCnt} / {totalCnt}</span>
        </div>
        <div className="mp-progress-track" style={{ height: 6, borderRadius: 3, background: '#efefec', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: pct + '%', background: progColor, borderRadius: 3, transition: 'width 0.4s' }} />
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{pct}% завершено</div>
      </div>

      {/* Фазы */}
      {[1, 2, 3, 4, 5].map(phase => {
        const phaseTasks = tasks.filter(t => t.phase === phase)
        if (phaseTasks.length === 0) return null
        const allDone = phaseTasks.every(t => pipelineMap[t.id]?.status === 'done')
        return (
          <div key={phase} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #f0f0ec' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: allDone ? '#639922' : '#1a1a1a', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{phase}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{PHASE_NAMES[phase]}</span>
              {allDone && <span style={{ fontSize: 11, color: '#639922', marginLeft: 'auto' }}>✓ Завершена</span>}
            </div>
            {phaseTasks.map(task => {
              if (task.id === 'housing_booking') return null
              const locked = isTaskLocked()
              const row = pipelineMap[task.id] || { status: 'pending', files: [], expense_entries: [] }

              if (ACCUMULATIVE_TASKS.includes(task.id)) {
                const GROUP_TASKS = ['passport_translation', 'medical_tests', 'snils', 'white_bg_photo']
                const canSendGroupAcc = currentUser?.role === 'admin' && GROUP_TASKS.includes(task.id)
                return (
                  <AccumulativeTaskCard
                    key={task.id}
                    task={task}
                    pipeline={row}
                    locked={locked}
                    candidateId={candidate.id}
                    onInProgress={handleInProgress}
                    onRevert={handleRevert}
                    onComplete={(t, p) => setActiveModal({ type: 'complete', task: t, pipeline: p, mode: 'complete' })}
                    onAddEntry={t => setActiveModal({ type: 'addEntry', task: t })}
                    onEditEntry={(t, i, e) => setActiveModal({ type: 'editEntry', task: t, index: i, entry: e })}
                    onDeleteEntry={(t, i) => setActiveModal({ type: 'editEntry', task: t, index: i })}
                    onAddFile={handleAddFile}
                    onSendToGroup={canSendGroupAcc ? (url, name, label) => sendPipelineFileToGroup(candidate.id, task.id, url, name, label) : undefined}
                  />
                )
              }

              const GROUP_TASKS = ['passport_translation', 'medical_tests', 'snils', 'white_bg_photo']
              const canSendGroup = currentUser?.role === 'admin' && GROUP_TASKS.includes(task.id)
              return (
                <TaskCard
                  key={task.id}
                  task={task}
                  pipeline={row}
                  locked={locked}
                  users={users}
                  currentUser={currentUser}
                  candidateId={candidate.id}
                  onInProgress={handleInProgress}
                  onRevert={handleRevert}
                  onComplete={(t, p) => setActiveModal({ type: 'complete', task: t, pipeline: p, mode: 'complete' })}
                  onEdit={(t, p) => setActiveModal({ type: 'complete', task: t, pipeline: p, mode: 'edit' })}
                  onAddFile={handleAddFile}
                  onAddExpense={['housing_rent', 'transport'].includes(task.id) ? () => setActiveModal({ type: 'addCandidateExpense', task }) : undefined}
                  onUpdateCost={handleUpdateCost}
                  onSendToGroup={canSendGroup ? (url, name, label) => sendPipelineFileToGroup(candidate.id, task.id, url, name, label) : undefined}
                />
              )
            })}
          </div>
        )
      })}

      {/* Завершить оформление / партнёрам */}
      {!candidate.moscow_completed && !candidate.partner_completed && (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0ec' }}>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={async () => {
                try {
                  await updateCandidate(candidate.id, { moscow_completed: true, moscow_completed_at: new Date().toISOString() })
                  await addActivityLog(`Кандидат ${candidate.name} завершил оформление в Москве`, currentUser.full_name || currentUser.id)
                  if (onComplete) onComplete()
                } catch (e) { alert('Ошибка: ' + e.message) }
              }}
              style={{ fontSize: 13, padding: '10px 24px', borderRadius: 8, border: 'none', background: '#639922', color: 'white', cursor: 'pointer', fontWeight: 600 }}
            >✅ Завершить оформление</button>
            <button
              onClick={async () => {
                try {
                  await updateCandidate(candidate.id, { partner_completed: true, partner_completed_at: new Date().toISOString() })
                  await addActivityLog(`Кандидат ${candidate.name} завершён партнёрам`, currentUser.full_name || currentUser.id)
                  if (onComplete) onComplete()
                } catch (e) { alert('Ошибка: ' + e.message) }
              }}
              style={{ fontSize: 13, padding: '10px 24px', borderRadius: 8, border: 'none', background: '#2e6da4', color: 'white', cursor: 'pointer', fontWeight: 600 }}
            >🤝 Завершить партнёрам</button>
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 6, textAlign: 'center' }}>Кандидат переходит в раздел «Завершённые» со всеми данными</div>
        </div>
      )}
      {candidate.moscow_completed && (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0ec', textAlign: 'center', color: '#639922', fontSize: 13, fontWeight: 600 }}>
          ✅ Оформление завершено · {candidate.moscow_completed_at ? new Date(candidate.moscow_completed_at).toLocaleDateString('ru-RU') : ''}
        </div>
      )}
      {candidate.partner_completed && (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0ec', textAlign: 'center', color: '#2e6da4', fontSize: 13, fontWeight: 600 }}>
          🤝 Завершено партнёрам · {candidate.partner_completed_at ? new Date(candidate.partner_completed_at).toLocaleDateString('ru-RU') : ''}
        </div>
      )}

      {/* Модалы */}
      {activeModal?.type === 'complete' && (
        <TaskCompleteModal
          task={activeModal.task}
          pipeline={activeModal.pipeline}
          candidateId={candidate.id}
          currentUser={currentUser}
          users={users}
          mode={activeModal.mode}
          onSave={handleSave}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal?.type === 'addEntry' && (
        <AddExpenseEntryModal
          task={activeModal.task}
          candidateId={candidate.id}
          onSave={handleAddExpenseEntry}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal?.type === 'editEntry' && (
        <EditExpenseEntryModal
          task={activeModal.task}
          index={activeModal.index}
          entry={activeModal.entry}
          candidateId={candidate.id}
          onSave={handleEditExpenseEntry}
          onDelete={handleDeleteExpenseEntry}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal?.type === 'addCandidateExpense' && (
        <AddExpenseEntryModal
          task={activeModal.task}
          candidateId={candidate.id}
          onSave={async (entry) => {
            const task = activeModal.task
            const row = pipelineMap[task.id] || {}
            const prevCost = parseFloat(row.cost) || 0
            const newCost = prevCost + entry.amount
            await updateMoscowTask(candidate.id, task.id, { cost: newCost, currency: entry.currency })
            setPipeline(prev => prev.map(r => r.task_id === task.id ? { ...r, cost: newCost, currency: entry.currency } : r))
            const label = task.name + (entry.note ? ': ' + entry.note : '')
            await addCandidateExpense(entry.amount, entry.currency, label)
            setActiveModal(null)
          }}
          onClose={() => setActiveModal(null)}
        />
      )}
    </div>
  )
}
